import AlternateSizeGatsbyImage from '@root/joinroot.com/src/components/alternate-size-gatsby-image';
import BodyCopy from '@root/joinroot.com/src/components/body-copy';
import Colors from '@root/joinroot.com/src/utils/colors';
import LinkButton from '@root/joinroot.com/src/components/buttons/link-button';
import PropTypes from '@root/vendor/prop-types';
import useVisibilityAnalytics from '@root/core/src/hooks/use-visibility-analytics';
import { H3 } from '@root/joinroot.com/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';

export default function ImageToutTile({
  body,
  button,
  image,
  mobileImage,
  title,
  id,
  buttonComponent = LinkButton,
}) {
  const { nodeRef, trackClick } = useVisibilityAnalytics(`IMAGE_TOUT_${id}`);
  const ButtonComponent = buttonComponent;

  return (
    <div
      css={[styles.imageTout, columnStyles.imageTout]}
      ref={nodeRef}
    >
      <div css={styles.imageContainer}>
        <AlternateSizeGatsbyImage
          desktop={image.fluid}
          imageAltText={image.imageAltText ?? ''}
          mobile={mobileImage && mobileImage.fluid}
        />
      </div>
      {(title || body || button) &&
        <div css={styles.textContainer}>
          {title && <H3 css={styles.title}>{title}</H3>}
          <BodyCopy
            content={body}
            css={styles.body}
          />
          {button &&
            <ButtonComponent
              onClick={() => trackClick(`CTA_${button.id}`)}
              to={button.link}
              type={button.type}
            >
              {button.text}
            </ButtonComponent>
          }
        </div>
      }
    </div>
  );
}

const styles = StyleSheet.create({
  imageTout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    marginBottom: 50,
    textAlign: 'center',
    '&:last-of-type': {
      paddingBottom: 0,
    },
    '.container-off-white &': {
      backgroundColor: Colors.nearWhite(),
    },
    '.sliding &': {
      marginRight: 20,
      width: 'auto',
      minWidth: 255,
      maxWidth: 335,
    },
    '.sliding.one-tout &': {
      marginRight: 0,
    },
    '@media screen and (min-width: 768px)': {
      '.two-touts &': {
        maxWidth: 'calc(50% - 10px)',
      },
      '.four-touts &': {
        maxWidth: 'calc(50% - 10px)',
      },
      '.four-plus-touts &': {
        maxWidth: 'calc(33% - 11px)',
      },
      '.four-touts &, .four-plus-touts &': {
        marginRight: 20,
      },
      '.four-touts &:nth-of-type(2n)': {
        marginRight: 0,
      },
      '.four-plus-touts &:nth-of-type(3n), .four-plus-touts &:last-of-type': {
        marginRight: 0,
      },
      '.sliding.four-touts &, .sliding.four-plus-touts &': {
        marginRight: 20,
        width: 335,
      },
    },
    '@media screen and (min-width: 1280px)': {
      '.three-touts &': {
        maxWidth: 'calc(33% - 10px)',
      },
      '.four-touts &, .four-plus-touts &': {
        maxWidth: 'calc(25% - 15px)',
      },
      '.four-touts &:nth-of-type(2n), .four-plus-touts &:nth-of-type(3n)': {
        marginRight: 20,
      },
      '.four-touts &:nth-of-type(4n), .four-plus-touts &:nth-of-type(4n)': {
        marginRight: 0,
      },
      '.sliding.four-plus-touts &:nth-of-type(4n)': {
        marginRight: 20,
      },
    },
    '@media screen and (min-width: 1440px)': {
      '.sliding.four-touts &:nth-of-type(4n), .sliding.four-plus-touts &:nth-of-type(4n)': {
        marginRight: 20,
      },
    },
  },
  imageContainer: {
    width: '100%',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 460,
    marginTop: 35,
    '@media screen and (min-width: 768px)': {
      '.one-tout & ': {
        maxWidth: 690,
      },
    },
  },
  title: {
    marginTop: 0,
    marginBottom: 15,
  },
  body: {
    marginBottom: 35,
    '&:last-child': {
      marginBottom: 0,
    },
  },
});

const columnStyles = StyleSheet.create({
  imageTout: {
    '.two-columns.one-tout &, .three-columns.one-tout &, .four-columns.one-tout &': {
      maxWidth: 925,
      marginRight: '0 !important',
    },
    '@media screen and (min-width: 768px)': {
      '.two-columns &, .four-columns &': {
        maxWidth: 'calc(50% - 10px)',
      },
      '.three-columns &': {
        maxWidth: 'calc(33% - 11px)',
      },
      '.two-columns &:nth-of-type(2n), .three-columns &:nth-of-type(3n), .four-columns &:nth-of-type(4n)': {
        marginRight: 0,
      },
      '.two-columns.three-touts &:nth-of-type(2n-1)': {
        marginRight: 20,
      },
      '.two-columns.four-plus-touts &': {
        marginRight: 20,
      },
      '.two-columns.four-plus-touts &:nth-of-type(2n)': {
        marginRight: 0,
      },
      '.three-columns.four-touts &:nth-of-type(2n)': {
        marginRight: 20,
      },
      '.four-columns &:nth-of-type(n)': {
        marginRight: 20,
      },
      '.four-columns &:nth-of-type(2n)': {
        marginRight: 0,
      },
      '.sliding.four-columns &:nth-of-type(2n)': {
        marginRight: 20,
      },
    },
    '@media screen and (min-width: 1280px)': {
      '.two-columns &': {
        maxWidth: 'calc(50% - 10px)',
      },
      '.three-columns &': {
        maxWidth: 'calc(33% - 10px)',
      },
      '.four-columns &': {
        maxWidth: 'calc(25% - 15px)',
      },
      '.three-columns &:nth-of-type(4n)': {
        marginRight: 20,
      },
      '.four-columns &:nth-of-type(2n)': {
        marginRight: 20,
      },
      '.four-columns &:nth-of-type(4n)': {
        marginRight: 0,
      },
    },
  },
});

ImageToutTile.propTypes = {
  body: PropTypes.node,
  button: PropTypes.shape({
    id: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  buttonComponent: PropTypes.func,
  id: PropTypes.string.isRequired,
  image: PropTypes.shape({
    imageAltText: PropTypes.string,
    fluid: PropTypes.object.isRequired,
  }).isRequired,
  mobileImage: PropTypes.shape({
    fluid: PropTypes.object.isRequired,
  }),
  title: PropTypes.node,
};
